import { useEffect, useState } from "react";
import { marked } from "marked";

import { Badge, Col, Container, Row } from "react-bootstrap";

import CardVendor from "../../components/CardVendor";
import CompanyFeedback from "../../components/Company/CompanyFeedback";
import CompanyReviews from "../../components/Company/CompanyReviews";
import CompanyFocus from "../../components/Company/CompanyFocus";
import CompanyLocation from "../../components/Company/CompanyLocation";
import CompanyOverview from "../../components/Company/CompanyOverview";
import CompanyPortfolio from "../../components/Company/CompanyPortfolio";
import SimilarCompanies from "../../components/Company/SimilarCompanies";
import SubMenu from "../../components/Company/SubMenu";
import ClaimProfileButton from "../../components/Company/ClaimProfileButton";
import { auth0 } from '../../lib/auth0';
import { catalogService, vendorService, companyService } from '../../services';

const groupBy = (xs, key) => {
  return xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

const groupValues = (values) => {
  const entries = new Map();
  for (let key in values) {
    let obj = groupBy(values[key], 'groupvalue')
    entries.set(key, obj);
  }
  return Object.fromEntries(entries);
}

const routerItem = (arr, code) => {
  const res = arr.filter((f) => f.group_code === code)
    .map((item) => (
      {
        id: item.category_map_id,
        group_code: item.group_code,
        parent_code: item.parent_code,
        code: item.code,
        url: item.url
      }))
  return res || []
}

export async function getServerSideProps({ params, req, res }) {
  const session = await auth0.getSession(req, res);
  const user = session?.user || null;

  const { slug = '' } = await params;
  const data = await vendorService.getProfileByName(slug);

  if (!data || data.length == 0) {
    return {
      notFound: true,
    }
  }

  const profile = data.find(p => p.published === true) || data[0];

  if (profile.published !== true) {
    return {
      notFound: true,
    }
  }

  const categoryMap = await catalogService.getCategoriesRouter();
  //await openstreetmapService.setMapFeatures(data[0]?.locations || [])

  profile.locations.forEach((loc) => { loc.features = [] })

  const { industries, categories, subcategories, platforms, subsystems } = profile;

  const categoryRouter = {
    categories: routerItem(categoryMap, 'category'),
    subcategories: routerItem(categoryMap, 'subcategory'),
    platforms: routerItem(categoryMap, 'platform'),
    industries: routerItem(categoryMap, 'industry'),
  }

  const initValues = {
    industries: industries || [],
    categories: categories || [],
    subcategories: subcategories || [],
    subsystems: subsystems || [],
    platforms: platforms || []
  };

  const focus = groupValues(initValues);
  //const meta = profile.company.company_description.split('.', 2)[0] || ""
  const meta = profile.company.company_description.slice(0, 159) || ""

  const arrCAT = focus.categories?.['CAT'] || []

  if (arrCAT.length > 0) {
    arrCAT.forEach(item => {
      item.url = categoryRouter.categories.find((f) => f.code === item.value)?.url || ""
    });
  }

  const arrIND = focus.industries?.['IND'] || []

  if (arrIND.length > 0) {
    arrIND.forEach(item => {
      item.url = categoryRouter.industries.find((f) => f.code === item.value)?.url || ""
    });
  }

  Object.keys(focus.subcategories).forEach((name) => {
    return focus.subcategories[name].forEach((item) => {
      item.url = categoryRouter.subcategories.find((f) => f.code === item.value)?.url || ""
    })
  })

  Object.keys(focus.platforms).forEach((name) => {
    return focus.platforms[name].forEach((item) => {
      item.url = categoryRouter.platforms.find((f) => f.code === item.value)?.url || ""
    })
  })

  let similarCompanies = [];
  try {
      similarCompanies = await companyService.getAlsoViewed(slug);
  } catch (e) {
      console.error("Failed to fetch similar companies:", e);
  }

  return {
    props: {
      nav: {
        light: true,
        classes: "shadow",
        color: "white",
      },
      title: `${profile.title} - Company Overview & Top Mentions`,
      hideDateSuffix: true,
      meta: meta,
      data: profile,
      focus: focus,
      slug: slug,
      user: user,
      similarCompanies: similarCompanies || [],
    },
  }
}

export const CompanyProfile = (props) => {
  const { company, last_update, portfolios, feedback, reviews } = props.data;

  const updated_date = new Date(last_update);
  const getMonthName = (dateObj) => dateObj.toLocaleString("default", { month: "long" })

  const [showSubMenu, setShowSubMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const needShowMenu = scrollTop > 100;
      setShowSubMenu(needShowMenu);

      const mainMenu = document.getElementById('megamenu');

      if (needShowMenu) {
        mainMenu.classList.remove('megamenu-visible');
        mainMenu.classList.add('megamenu-hidden');
      } else {
        mainMenu.classList.remove('megamenu-hidden');
        mainMenu.classList.add('megamenu-visible');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <>
      <SubMenu company={company} show={showSubMenu} reviews={reviews} />
      <section className="py-5">
        <Container>
          <Row >
            <Col lg="9">
              <div id="about">
                <h1 className="hero-heading mb-0">{company.company_name}</h1>
                <ClaimProfileButton company={props.data} user={props.user} slug={props.slug} />
                <div className="text-block-5 mb-3">
                  <p>
                    <Badge bg="primary-light" text="primary">{`Updated in ${getMonthName(updated_date)}, ${updated_date.getFullYear() }`}</Badge>
                  </p>
                  <div className="text-muted text-md" dangerouslySetInnerHTML={{ __html: marked.parse(company.company_description || "") }} />
                </div>
                <CompanyOverview data={props.data} />
              </div>
            </Col>
            <Col lg="3" className="me-lg-auto">
              <CardVendor data={props.data} />
            </Col>
          </Row>

          <CompanyFocus data={props.focus} />
          {feedback &&
            <CompanyFeedback data={feedback} />
          }
          {reviews && reviews.length > 0 &&
            <CompanyReviews reviews={reviews} company={company} />
          }
          <CompanyLocation data={props.data} />
          {props.similarCompanies && props.similarCompanies.length > 0 && 
            <SimilarCompanies data={props.similarCompanies} />
          }
          {(portfolios || []).length > 0 &&
            <CompanyPortfolio data={portfolios} />
          }
        </Container>
      </section>
    </>
  )
}

export default CompanyProfile